// src/app/pages/definition/personnel/PersonnelPage.tsx
import React, {FC, useCallback, useEffect, useState} from 'react'
import {Form, Formik, FormikProps} from 'formik'
import {
  initialPersonnel,
  tabOptions,
  IPersonnelModel,
  validationPersonnel,
  IN_OR_OUT,
} from './PersonnelModel'
import {createPersonnel, deletePersonnel, loadPersonnels, updatePersonnel} from './Api'
import {ConfirmModal} from '../../../../shared/components/ConfirmModal'
import {ToastContainer} from 'react-toastify' 
import FormTabHeader from '../../../../shared/components/FormTabHeader'
import TextInput from '../../../../shared/components/TextInput'
import SelectInput from '../../../../shared/components/SelectInput'
import TextAreaInput from '../../../../shared/components/TextAreaInput'
import PhoneInput from '../../../../shared/components/PhoneInput'
import CheckboxInput from '../../../../shared/components/CheckboxInput'
import {showErrorToast, showSuccessToast} from '../../../../shared/components/ToastUtils'
import {PersonnelTable} from './PersonnelTable'
import { LICENSE_CLASS, VISIBLE_OR_NOT } from '../../../../shared/models/SharedConstantLookups'
import { branches, users } from '../../../../shared/models/SharedApiLookups'


export const PersonnelPage: FC = () => {
  const [tab, setTab] = useState<'form' | 'list'>('form')
  const [personnels, setPersonnels] = useState<IPersonnelModel[]>([])
  const [loading, setLoading] = useState(false)
  const [selectedId, setSelectedId] = useState<string | null | undefined>(null)
  const [branchOptions, setBranchOptions] = useState<{value: string; label: string}[]>([])
  const [userOptions, setUserOptions] = useState<{value: string; label: string}[]>([])
  const [showModal, setShowModal] = useState(false)
  const [currentPage, setCurrentPage] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [sortBy, setSortBy] = useState<string>('')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc')
  const [pageSize, setPageSize] = useState(10)

  const fetchPersonnels = useCallback(
    async (page: number = 0, sort?: string, order?: 'asc' | 'desc') => {
      try {
        const res = await loadPersonnels(page, pageSize, sort, order)
        const data = res.data.payload || res.data
        setPersonnels(data?.content ?? [])
        setTotalPages(data?.totalPages ?? 1)
      } catch (err: any) {
        if (err?.response?.status === 401) {
          window.location.href = '/login'
          return
        }
        console.error(err)
        showErrorToast('Personeller yüklenirken bir hata oluştu')
      }
    },
    [pageSize]
  )

  useEffect(() => {
    fetchPersonnels(currentPage)
  }, [fetchPersonnels, currentPage])

  useEffect(() => {
    Promise.all([branches(), users()]).then(
      ([brn, usr]) => { 
        setBranchOptions(brn)
        setUserOptions(usr)
      }
    )
  }, [])

  const handleSave = async (values: IPersonnelModel, resetForm: () => void) => {
    setLoading(true)
    try {
      if (selectedId) {
        await updatePersonnel({...values, id: selectedId})
        showSuccessToast('Personel başarıyla güncellendi')
      } else {
        await createPersonnel(values)
        showSuccessToast('Personel başarıyla oluşturuldu')
      }
      setCurrentPage(0)
      await fetchPersonnels(0)
      resetForm()
      setSelectedId(null)
      setTab('list')
    } catch (err) {
      console.error(err)
      showErrorToast('Bir hata oluştu')
    } finally {
      setLoading(false)
    }
  }

  const handleEdit = (p: IPersonnelModel, formik: FormikProps<IPersonnelModel>) => {
    formik.setValues(p)
    setSelectedId(p.id)
    setTab('form')
  }

  const handleDeleteClick = useCallback((p: IPersonnelModel) => {
    setSelectedId(p.id)
    setShowModal(true)
  }, [])

  const handleDelete = async () => {
    if (!selectedId) return
    try {
      await deletePersonnel(selectedId)
      showSuccessToast('Personel başarıyla silindi')
      await fetchPersonnels(currentPage)
    } catch (err) {
      console.error(err)
      showErrorToast('Personel silinirken bir hata oluştu')
    } finally {
      setShowModal(false)
      setSelectedId(null)
    }
  }

  const handlePageChange = useCallback(
    (newPage: number) => {
      if (newPage >= 0 && newPage < totalPages) {
        setCurrentPage(newPage)
        fetchPersonnels(newPage, sortBy, sortOrder)
      }
    },
    [totalPages, sortBy, sortOrder, fetchPersonnels]
  )

  const handleSort = useCallback(
    (column: string, order: 'asc' | 'desc') => {
      setSortBy(column)
      setSortOrder(order)
      fetchPersonnels(currentPage, column, order)
    },
    [currentPage, fetchPersonnels]
  )

  return (
    <>
      <Formik
        initialValues={initialPersonnel}
        validationSchema={validationPersonnel}
        enableReinitialize
        onSubmit={(values, {resetForm}) => handleSave(values, resetForm)}
      >
        {(formik) => {
          const handleReset = () => {
            formik.resetForm()
            setSelectedId(null)
          }

          return (
            <>
              <FormTabHeader
                formId='personnelForm'
                formikSubmit={() => formik.submitForm()}
                formikReset={handleReset}
                loading={loading}
                tab={tab}
                setTab={(k) => setTab(k as 'form' | 'list')}
                setSelectedId={setSelectedId}
                tabs={tabOptions}
                onExportExcel={() => console.log('Excel export')}
              />

              {tab === 'form' && (
                <div className='card mb-5'>
                  <Form id='personnelForm' className='form card-body'>
                    <div className='row g-6'>
                      <div className='col-lg-6'>
                        <div className='row g-3'>
                          <TextInput
                            name='tcIdentityNo'
                            label='TC Kimlik No'
                            wrapperClassName='col-12'
                          />
                          <TextInput
                            name='firstName'
                            label='Ad *'
                            placeholder='Ad'
                            wrapperClassName='col-12'
                          />
                          <TextInput
                            name='lastName'
                            label='Soyad *'
                            placeholder='Soyad'
                            wrapperClassName='col-12'
                          />
                          <TextAreaInput
                            name='address'
                            label='Ev Adresi'
                            rows={3}
                            wrapperClassName='col-12'
                          />
                          <PhoneInput
                            name='homePhone'
                            label='Ev Telefonu'
                            wrapperClassName='col-12'
                          />
                          <PhoneInput
                            name='workPhone'
                            label='İş Telefonu'
                            wrapperClassName='col-12'
                          />
                          <TextInput
                            name='startDate'
                            type='date'
                            label='İş Giriş Tarihi'
                            wrapperClassName='col-6'
                          />
                          <TextInput
                            name='endDate'
                            type='date'
                            label='İş Çıkış Tarihi'
                            wrapperClassName='col-6'
                          />
                          <TextInput name='reference' label='Referans' wrapperClassName='col-12' />
                          <TextAreaInput
                            name='notes'
                            label='Açıklama'
                            rows={3}
                            wrapperClassName='col-12'
                          />
                          <TextInput
                            name='driverLicenseNo'
                            label='Sürücü Belge No'
                            wrapperClassName='col-8'
                          />
                          <TextInput name='bloodType' label='Kan Grubu' wrapperClassName='col-4' />
                          <SelectInput
                            name='driverLicenseClass'
                            label='Sürücü Sınıfı'
                            wrapperClassName='col-md-4'
                            options={LICENSE_CLASS}
                          />
                          <TextInput
                            name='issueDate'
                            type='date'
                            label='Veriliş Tarihi'
                            wrapperClassName='col-md-4'
                          />
                          <TextInput
                            name='issuePlace'
                            label='Veriliş Yeri'
                            wrapperClassName='col-md-4'
                          />
                        </div>
                      </div>

                      <div className='col-lg-6'>
                        <div className='row g-3'>
                          <PhoneInput name='gsm' label='Gsm' wrapperClassName='col-12' />
                          <TextInput
                            name='email'
                            type='email'
                            label='Mail Adresi'
                            wrapperClassName='col-12'
                          />
                          <TextInput
                            name='birthDate'
                            type='date'
                            label='Doğum Tarihi'
                            wrapperClassName='col-12'
                          />
                          <TextInput
                            name='birthPlace'
                            label='Doğum Yeri'
                            wrapperClassName='col-12'
                          />
                          <TextInput name='fatherName' label='Baba Adı' wrapperClassName='col-12' />
                          <TextInput name='motherName' label='Ana Adı' wrapperClassName='col-12' />
                          <TextInput
                            name='identityNo'
                            label='Kimlik No'
                            wrapperClassName='col-12'
                          />
                          <TextInput
                            name='registrationCity'
                            label='Kayıtlı İl'
                            wrapperClassName='col-6'
                          />
                          <TextInput
                            name='registrationDistrict'
                            label='Kayıtlı İlçe'
                            wrapperClassName='col-6'
                          />
                          <TextInput
                            name='neighborhoodVillage'
                            label='Mahalle-Köy'
                            wrapperClassName='col-12'
                          />
                          <TextInput name='volumeNo' label='Cilt No' wrapperClassName='col-4' />
                          <TextInput
                            name='familyOrderNo'
                            label='Aile Sıra No'
                            wrapperClassName='col-4'
                          />
                          <TextInput name='orderNo' label='Sıra No' wrapperClassName='col-4' />
                          <SelectInput
                            name='branch'
                            label='Şube'
                            options={branchOptions}
                            wrapperClassName='col-md-12'
                          />
                          <SelectInput
                            name='role'
                            label='Görevi'
                            options={IN_OR_OUT}
                            wrapperClassName='col-md-6'
                          /> 
                          <SelectInput
                            name='userId'
                            label='Kullanıcı'
                            options={userOptions}
                            wrapperClassName='col-md-6'
                          />
                          <CheckboxInput
                            name='isInactive'
                            label='Pasif Et'
                            wrapperClassName='col-md-12'
                          />
                        </div>
                      </div>
                    </div>
                  </Form>
                </div>
              )}

              {tab === 'list' && (
                <div className='card'>
                  <div className='card-body'>
                    <PersonnelTable
                      data={personnels}
                      currentPage={currentPage}
                      totalPages={totalPages}
                      onPageChange={handlePageChange}
                      onEdit={(p) => handleEdit(p, formik)}
                      onDelete={handleDeleteClick}
                      sortBy={sortBy}
                      sortOrder={sortOrder}
                      onSort={handleSort}
                      loading={loading}
                    />
                  </div>
                </div>
              )}
            </>
          )
        }}
      </Formik>

      <ConfirmModal
        show={showModal}
        header='Silme Onayı'
        text='Bu personeli silmek istediğinize emin misiniz?'
        handleClose={() => {
          setShowModal(false)
          setSelectedId(null)
        }}
        applyOnClick={handleDelete}
      />

      <ToastContainer position='top-right' autoClose={5000} newestOnTop pauseOnHover />
    </>
  )
}
